/**
 * Compte à rebours « léger » du Live School, utilisable SANS être connecté.
 *
 * Réutilise la même source que le player (`live-state`, edge fn PUBLIQUE) et les
 * helpers purs `@jeraisonne/db/live`, mais SANS aucun effet de bord (pas de join,
 * pas de déchiffrement, pas de clés) — juste l'état temporel pour afficher le
 * compte à rebours sur l'écran d'inscription (`/live/<niveau>` non connecté).
 *
 * Partage le cache React Query (`['live-state', niveau]`) avec `useLiveQuiz`.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { computeClockDelta, getQuizState, type LiveQuizStatus } from '@jeraisonne/db/live';
import { supabase } from '@/lib/supabase';

interface LiveStateResponse {
  server_time: number;
  challenge_id?: string;
  title?: string | null;
  starts_at?: string;
  step_duration_ms?: number;
  step_count?: number;
}

export interface LiveCountdown {
  loading: boolean;
  /** null tant qu'aucun défi n'est programmé pour ce niveau. */
  status: LiveQuizStatus | null;
  /** Temps restant (ms) avant le départ (WAITING) — 0 dans les autres états. */
  timeLeftMs: number;
  title: string | null;
  stepCount: number;
}

/** Vue à afficher sur l'écran d'inscription selon l'état du défi (pur, testé). */
export type SignupCountdownView = 'countdown' | 'live' | 'default';
export function signupCountdownView(status: LiveQuizStatus | null): SignupCountdownView {
  if (status === 'WAITING') return 'countdown';
  if (status === 'LIVE') return 'live';
  return 'default';
}

export function useLiveCountdown(niveau: string): LiveCountdown {
  const stateQuery = useQuery({
    queryKey: ['live-state', niveau],
    refetchInterval: 30_000,
    staleTime: 10_000,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke<LiveStateResponse>('live-state', {
        body: { level: niveau },
      });
      if (error) throw error;
      return { ...(data as LiveStateResponse), clientReceiveTime: Date.now() };
    },
  });

  const data = stateQuery.data;
  const challengeId = data?.challenge_id ?? null;
  const stepDurationMs = data?.step_duration_ms ?? 30_000;
  const stepCount = data?.step_count ?? 0;

  const serverDelta = useMemo(
    () =>
      data
        ? computeClockDelta({
            serverTime: data.server_time,
            clientReceiveTime: data.clientReceiveTime,
          })
        : 0,
    [data],
  );

  // Tick d'horloge : recalcule l'état localement chaque demi-seconde.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, []);

  const live = useMemo(() => {
    if (!data?.starts_at || !challengeId) return null;
    return getQuizState({
      serverTime: now + serverDelta,
      startsAt: new Date(data.starts_at).getTime(),
      stepDurationMs,
      stepCount,
    });
  }, [data?.starts_at, challengeId, now, serverDelta, stepDurationMs, stepCount]);

  return {
    loading: stateQuery.isLoading,
    status: live?.status ?? null,
    timeLeftMs: live?.status === 'WAITING' ? live.timeLeftMs : 0,
    title: data?.title ?? null,
    stepCount,
  };
}
